import { HttpClient, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AjaxService } from '../services/ajax.service';
import { AuthStateService } from '../services/auth-state.service';
import { REFRESH_TOKEN, TWO_FACTOR_VERIFY } from '../services/account-api.constants';

// Shared across concurrent requests so a burst of parallel 401s triggers a
// single POST /api/login/refresh instead of one per request.
let refreshResult$: BehaviorSubject<boolean | null> | null = null;

// Catches a 401 from a protected endpoint (now enforced on every controller via the global
// [Authorize] fallback policy), silently rotates the session via POST /api/login/refresh
// (refresh-token cookie only), and retries the original request once. On refresh failure it
// clears the client-side auth state and redirects to the public landing page - "not logged
// in" always lands there, never on the login form (see ExitService.cleanExit).
export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
	const http = inject(HttpClient);
	const router = inject(Router);
	const ajaxService = inject(AjaxService);
	const authStateService = inject(AuthStateService);

	const refreshUrl = ajaxService.getBaseOfApi() + REFRESH_TOKEN;
	const twoFactorVerifyUrl = ajaxService.getBaseOfApi() + TWO_FACTOR_VERIFY;
	// Only these two must be excluded from the refresh-retry dance: the refresh call itself
	// (self-recursion) and TwoFactorVerify (its 401 means "bad/expired 2FA code", never "access
	// token expired" - the caller needs to see that 401 directly, not get silently redirected).
	// Every OTHER endpoint under /api/login/* (me, sessions, logout, 2fa/status, ...) is a
	// normal [Authorize]-protected endpoint and must go through the retry path like any other.
	const isAuthEndpoint = req.url === refreshUrl || req.url === twoFactorVerifyUrl;

	return next(req).pipe(
		catchError((error: unknown) => {
			if (!(error instanceof HttpErrorResponse) || error.status !== 401 || isAuthEndpoint) {
				return throwError(() => error);
			}

			if (!refreshResult$) {
				const subject = new BehaviorSubject<boolean | null>(null);
				refreshResult$ = subject;

				http.post(refreshUrl, {}, { withCredentials: true }).subscribe({
					next: () => subject.next(true),
					error: () => subject.next(false),
				});
			}

			const inFlight = refreshResult$;

			return inFlight.pipe(
				filter((ok) => ok !== null),
				take(1),
				switchMap((ok) => {
					if (refreshResult$ === inFlight) {
						refreshResult$ = null;
					}
					if (ok) {
						return next(req);
					}
					authStateService.clear();
					router.navigate(['/landing']);
					return throwError(() => error);
				})
			);
		})
	);
};
